import { Fragment, memo, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { GetHydratedCallRecordResponse } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/phone_records_api.pb';
import {
  HydratedVoicemail,
  ViewStatus,
  VoicemailWithCallRecord,
} from '@weave/schema-gen-ts/dist/schemas/phone-exp/voicemail/voicemail.pb';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import {
  VoicemailMessageAlertEvent,
  VoicemailTagsAlertEvent,
} from '@weave/schema-gen-ts/dist/shared/phone/v1/voicemail/message.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { omit } from 'lodash-es';
import { useMutation, useQueryClient } from 'react-query';
import { PhoneCallsApi } from '@frontend/api-phone-calls';
import { VoicemailApi, VoicemailTypes as VmTypes } from '@frontend/api-voicemails';
import { getWeaveToken, hasSchemaACL } from '@frontend/auth-helpers';
import { ActionableListRow } from '@frontend/components';
import { getInitialParams } from '@frontend/env';
import { Icon } from '@frontend/icons';
import { useAppScopeStore, useScopedQuery } from '@frontend/scope';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { theme } from '@frontend/theme';
import { Dot, ListRow, useAlert } from '@frontend/design-system';
import { AllowedHistoricalNotificationPayloads, UpdatedNotificationAlertType } from '../historical-notification-list';
import { useNotificationContext } from '../notification-provider';
import { PaletteColors } from './filter-chips/constants';

export type HistoricalNotificationType<T extends keyof AllowedHistoricalNotificationPayloads> = {
  notification: UpdatedNotificationAlertType<T>;
  icon?: React.ReactNode;
  closeModal: () => void;
};

export const getNoticationTypeTextColor = (
  notificationType: string,
  showColorChipForYou: boolean,
  forYouNotificationColors: Record<string, string>
) => {
  if (showColorChipForYou) {
    return PaletteColors[forYouNotificationColors[notificationType]].textColor;
  }

  switch (notificationType) {
    case NotificationType.SMS:
      return theme.colors.tangerine40;

    case NotificationType.TEAM_CHAT:
      return theme.colors.indigo50;

    case NotificationType.PAYMENTS:
      return theme.colors.success50;

    case NotificationType.SCHEDULE_REQUEST:
      return theme.colors.rose50;

    case NotificationType.FORMS:
      return theme.colors.primary50;

    case NotificationType.REVIEWS:
    case NotificationType.REVIEW_COMMENT:
      return theme.colors.neutral50;

    case NotificationType.FAX:
      return theme.colors.warning50;

    case NotificationType.VOICEMAIL_MESSAGE:
      return theme.colors.secondary.eggplant50;

    default:
      return theme.colors.critical40;
  }
};

export const getNoticationTypeColor = (
  notificationType: string,
  showColorChipForYou: boolean,
  forYouNotificationColors: Record<string, string>
) => {
  if (showColorChipForYou) {
    return PaletteColors[forYouNotificationColors[notificationType]].backgroundColor;
  }

  switch (notificationType) {
    case NotificationType.SMS:
      return theme.colors.tangerine5;

    case NotificationType.TEAM_CHAT:
      return theme.colors.indigo5;

    case NotificationType.PAYMENTS:
      return theme.colors.success5;

    case NotificationType.SCHEDULE_REQUEST:
      return theme.colors.rose5;

    case NotificationType.FORMS:
      return theme.colors.primary5;

    case NotificationType.REVIEWS:
    case NotificationType.REVIEW_COMMENT:
      return theme.colors.neutral5;

    case NotificationType.FAX:
      return theme.colors.warning5;

    case NotificationType.VOICEMAIL_MESSAGE:
      return theme.colors.secondary.eggplant5;

    default:
      return theme.colors.critical5;
  }
};

export const getTitleDisplayName = (notificationType: string) => {
  switch (notificationType) {
    case NotificationType.SMS:
      return 'Messages';

    case NotificationType.PAYMENTS:
      return 'Payments';

    case NotificationType.SCHEDULE_REQUEST:
      return 'Schedule Requests';

    case NotificationType.FORMS:
      return 'Forms';

    case NotificationType.REVIEWS:
    case NotificationType.REVIEW_COMMENT:
      return 'Reviews';

    case NotificationType.FAX:
      return 'Fax';

    case NotificationType.VOICEMAIL_MESSAGE:
      return 'Voicemail';

    default:
      return notificationType;
  }
};

export const getIcon = (notificationType: string) => {
  switch (notificationType) {
    case NotificationType.SMS:
      return <Icon name='message-unread-small' />;

    case NotificationType.TEAM_CHAT:
      return <Icon name='chat-small' />;

    case NotificationType.PAYMENTS:
      return <Icon name='payments-small' />;

    case NotificationType.SCHEDULE_REQUEST:
      return <Icon name='calendar-small' />;

    case NotificationType.FORMS:
      return <Icon name='forms-small' />;

    case NotificationType.REVIEWS:
    case NotificationType.REVIEW_COMMENT:
      return <Icon name='star-small' />;

    case NotificationType.FAX:
      return <Icon name='fax-small' />;

    default:
      return <Icon name='label-small' />;
  }
};

export const CurvedDashedLine = () => {
  return (
    <div
      css={{
        position: 'absolute',
        top: theme.spacing(5),
        left: theme.spacing(3.25),
        height: `calc(100% - ${theme.spacing(8)})`,
        width: theme.spacing(3),
        borderBottomLeftRadius: theme.spacing(1),
        borderLeft: `1px dashed ${theme.colors.neutral30}`,
        borderBottom: `1px dashed ${theme.colors.neutral30}`,
      }}
    />
  );
};

type MoveToTimelineBlockProps = {
  notificationTypeColor: string;
  notificationTypeTextColor: string;
};

export const RenderMoveToTimelineBlock = memo(
  ({ notificationTypeColor, notificationTypeTextColor }: MoveToTimelineBlockProps) => (
    <div
      css={{
        position: 'absolute',
        height: '100%',
        width: '100%',
        top: '0px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: theme.spacing(1.5),
        fontWeight: theme.font.weight.bold,
        background: notificationTypeColor,
        color: notificationTypeTextColor,
        borderBottom: `1px solid ${theme.colors.neutral20}`,
      }}
    >
      {t('Moved to Timeline')}
    </div>
  )
);

export const HistoricalNotification = ({
  hasRead,
  icon,
  notificationAction,
  subtitle,
  title,
  trackingId,
  createdAt,
  children,
  titlePrefix,
  isSMSThreadExpanded,
  wrapperStyles,
  notificationHovered,
}: {
  createdAt: string;
  hasRead: boolean;
  icon?: React.ReactNode;
  notificationAction?: () => void;
  subtitle?: JSX.Element | string;
  title: string;
  trackingId?: string;
  children?: ReactNode;
  titlePrefix?: JSX.Element;
  isSMSThreadExpanded?: boolean;
  wrapperStyles?: Record<string, string>;
  notificationHovered?: boolean;
}) => {
  const isUnread = !hasRead;
  return (
    <ActionableListRow
      trackingId={trackingId}
      css={[
        css`
          padding-bottom: ${theme.spacing(3)};
          border-bottom: ${isSMSThreadExpanded && 'none'};
          background-color: ${isSMSThreadExpanded && theme.colors.neutral5};
        `,
        wrapperStyles,
      ]}
      {...(notificationAction && {
        onClick: () => notificationAction(),
      })}
      Lead={
        icon ? (
          <ListRow.Lead
            css={css`
              position: relative;
              padding-right: ${theme.spacing(1)};
              align-items: flex-start;
              margin-top: ${theme.spacing(0.5)};
            `}
          >
            {icon}
          </ListRow.Lead>
        ) : (
          <Fragment />
        )
      }
      TitlePrefix={titlePrefix}
      Title={
        <ListRow.Content.Title
          css={{
            display: 'flex',
            fontWeight: theme.font.weight.bold,
            fontSize: theme.spacing(1.75),
            lineHeight: theme.spacing(2.5),
            alignItems: 'center',
          }}
        >
          {isUnread && (
            <Dot
              color='primary'
              css={{ position: 'relative', height: theme.spacing(1.5), width: theme.spacing(1.5) }}
            />
          )}
          {title}
        </ListRow.Content.Title>
      }
      Content={
        <>
          {children}
          {!!subtitle && (
            <ListRow.Content.TertiaryTitle
              css={[
                css`
                  font-size: ${theme.fontSize(14)};
                `,
              ]}
            >
              {subtitle}
            </ListRow.Content.TertiaryTitle>
          )}
        </>
      }
      timestamp={dayjs(createdAt).toString()}
      timestampOverride={notificationHovered ? ' ' : undefined}
    />
  );
};

export const getUnreadCommentsText = (isSectionExpanded: boolean) => {
  const expandTextStyles = css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
  `;
  return (
    <div css={expandTextStyles}>
      {isSectionExpanded ? 'Hide messages' : 'Unread messages'}
      <Icon name={isSectionExpanded ? 'caret-up-tiny' : 'caret-down-tiny'} />
    </div>
  );
};

// hook to be used by phones component only
export const usePhoneRecordNotificationAction = ({
  notification,
  callRecordId,
  locationId,
  startedAt,
  ...props
}: HistoricalNotificationType<NotificationType.MISSED_CALL | NotificationType.CALL_RECORD_TAG> & {
  callRecordId: string;
  locationId: string;
  startedAt: string;
}) => {
  const alert = useAlert();
  const queryClient = useQueryClient();
  const notificationTrayContext = useNotificationContext();
  const { setShow } = useSlidePanelShallowStore('setShow');
  const { selectedLocationIds } = useAppScopeStore();

  const hasAccesstoCallRecordingMap = useMemo(() => {
    return selectedLocationIds.reduce((accessMap, locationId) => {
      accessMap[locationId] = hasSchemaACL(locationId, Permission.CALL_RECORDING_READ);
      return accessMap;
    }, {} as Record<string, boolean>);
  }, [selectedLocationIds]);

  const hasAccesstoCallRecording = useMemo(() => {
    return Object.values(hasAccesstoCallRecordingMap).some((hasAccess) => hasAccess);
  }, [hasAccesstoCallRecordingMap]);

  const optimisticApplyTag = useCallback(
    ({
      oldData,
      tag,
      channelId,
    }: {
      oldData: GetHydratedCallRecordResponse | undefined;
      tag: Tag;
      channelId: string;
    }) => {
      const oldRecord = oldData?.record;
      if (oldRecord && oldRecord.channelId === channelId) {
        return {
          record: {
            ...oldRecord,
            tagIds: oldRecord.tagIds ? [...oldRecord.tagIds, tag.id] : [tag.id],
          },
        };
      }

      return undefined;
    },
    []
  );

  const optimisticRemoveTag = useCallback(
    ({
      oldData,
      tag,
      channelId,
    }: {
      oldData: GetHydratedCallRecordResponse | undefined;
      tag: Tag;
      channelId: string;
    }) => {
      const oldRecord = oldData?.record;
      if (oldRecord && oldRecord.channelId === channelId) {
        return {
          record: {
            ...oldRecord,
            tagIds: oldRecord.tagIds?.filter((tg) => tg !== tag.id) || [],
          },
        };
      }

      return undefined;
    },
    []
  );

  const fetchCallRecordTagDetails = (queryKey: string[]) => {
    const recordData = queryClient.getQueryData([selectedLocationIds, ...queryKey]) as GetHydratedCallRecordResponse;
    return recordData?.record?.tagIds || [];
  };

  const notificationAction = async () => {
    if (!notification.hasRead) {
      notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
    }
    if (!notification.alert.payload) return;

    const { payload: params } = notification.alert;
    props.closeModal();
    try {
      const callRecordData = await queryClient.fetchQuery({
        queryKey: [selectedLocationIds, 'call-record-tag-event-details', params?.callRecordId, params?.locationId],
        queryFn: () =>
          PhoneCallsApi.getCallRecord({
            callRecordId: params.callRecordId,
            locationId: params.locationId,
            startTime: params.startedAt,
            endTime: params.startedAt,
            includeCallRecording: true,
          }),
      });

      setShow(true, 'callDetails', {
        rows: callRecordData.record ? [{ ...callRecordData.record, tags: [] }] : [],
        currentRowId: callRecordData?.record?.id,
        instanceId: null,
        hasAccessToRecordings: hasAccesstoCallRecording,
        toggleRowSelected: () => {},
        callDetailsQueryKey: ['call-record-tag-event-details', params?.callRecordId, params?.locationId],
        optimisticApplyTag,
        optimisticRemoveTag,
        sectionFocus: 'details',
        fetchTagData: fetchCallRecordTagDetails,
      });
    } catch (error) {
      alert.error('Error fetching call record data');
    }
  };

  return { notificationAction };
};

export const useVoicemailNotificationAction = ({
  notification,
  voicemailId,
  locationId,
  voicemailCreatedAt,
  ...props
}: HistoricalNotificationType<NotificationType.VOICEMAIL_TAG | NotificationType.VOICEMAIL_MESSAGE> & {
  voicemailId: string;
  locationId: string;
  voicemailCreatedAt: string;
}) => {
  const alert = useAlert();
  const weaveToken = getWeaveToken();
  const currentEnv = getInitialParams().backendApi;
  const notificationTrayContext = useNotificationContext();
  const queryClient = useQueryClient();
  const { selectedLocationIds } = useAppScopeStore();
  const {
    show: showPanel,
    setShow,
    context,
    panelType,
  } = useSlidePanelShallowStore('setShow', 'show', 'context', 'panelType');

  const [pendingViewParams, setPendingViewParams] = useState<
    VoicemailTagsAlertEvent | VoicemailMessageAlertEvent | null
  >(null);

  const generateMediaPath = (message: VoicemailWithCallRecord | undefined, locationId: string) => {
    const mediaId = message?.forwardedMessageId || message?.mediaId;

    if (!mediaId) {
      return '';
    }
    const mediaPath = `${currentEnv}/phone-exp/voicemail/download/${mediaId}?token=${weaveToken}&location_id=${locationId}`;
    return mediaPath;
  };

  // fetch voicemail details
  const voicemailDetailsQuery = useScopedQuery({
    queryKey: ['voicemail-event-panel-details', pendingViewParams?.voicemailMessageId, pendingViewParams?.locationId],
    queryFn: async () => {
      const { voicemail: { message, person, tagIds } = {} as HydratedVoicemail } = await VoicemailApi.getVoicemail({
        messageId: pendingViewParams?.voicemailMessageId ?? '',
      });
      return {
        firstName: person?.firstName ?? '',
        lastName: person?.lastName ?? '',
        contactName: pendingViewParams?.personContact,
        contactId: message?.personId ?? person?.personId,
        locationIds: pendingViewParams?.locationId ? [pendingViewParams.locationId] : [],
        channelId: message?.channelId ?? '',
        mailboxId: message?.mailboxId ?? '',
        voiceMailBoxName: pendingViewParams?.mailboxName,
        forwardedMessageId: message?.forwardedMessageId,
        readAt: message?.readAt,
        dateTime: dayjs(message?.createdAt).format('MMM DD YYYY, hh:mm A'),
        mediaFilePath: generateMediaPath(message, selectedLocationIds?.[0]),
        mediaId: message?.mediaId,
        contactNumber: message?.callerNumber || pendingViewParams?.callerNumber,
        voicemailId: message?.voicemailId,
        createdAt: message?.createdAt,
        tagIds: tagIds,
        tags: [],
      };
    },
    onError: () => {
      alert.error('Error fetching voicemail data');
    },
    enabled: Boolean(pendingViewParams?.voicemailMessageId),
  });

  const optimisticApplyTag = useCallback(
    ({
      oldData,
      tag,
      voicemailId,
    }: {
      oldData: typeof voicemailDetailsQuery.data | undefined;
      tag: Tag;
      voicemailId: string;
    }) => {
      if (oldData) {
        if (oldData.voicemailId === voicemailId) {
          return {
            ...oldData,
            tagIds: oldData.tagIds ? [...oldData.tagIds, tag.id] : [tag.id],
          };
        }
      }
      return undefined;
    },
    []
  );
  const optimisticRemoveTag = useCallback(
    ({
      oldData,
      tag,
      voicemailId,
    }: {
      oldData: typeof voicemailDetailsQuery.data | undefined;
      tag: Tag;
      voicemailId: string;
    }) => {
      if (oldData && oldData.voicemailId === voicemailId) {
        return {
          ...oldData,
          tagIds: oldData.tagIds ? oldData.tagIds.filter((tg) => tg !== tag.id) : [],
        };
      }
      return undefined;
    },
    []
  );

  // toggle voicemail status
  const { mutateAsync: updateReadStatusAsync } = useMutation(
    (payload: VmTypes.StatusPayload) => {
      const req = omit(payload, 'index');
      return VoicemailApi.setVoicemailViewStatus(req);
    },
    {
      onSuccess: (response, { status }) => {
        const queryKey = voicemailDetailsQuery.queryKey;
        if (!queryKey) {
          return;
        }
        const data = queryClient.getQueryData<typeof voicemailDetailsQuery.data>([selectedLocationIds, ...queryKey]);

        if (data) {
          const updatedData = {
            ...data,
            readAt:
              status === ViewStatus.VIEW_STATUS_READ && response.voicemail?.readEpoch
                ? // @ts-ignore - This type is wrong, the schema says it should be a number but it isn't
                  new Date(parseInt(response.voicemail.readEpoch)).toISOString()
                : undefined,
          };
          queryClient.setQueryData([selectedLocationIds, ...queryKey], updatedData);
        }
      },
    }
  );

  const { mutate: onDelete } = useMutation(
    (req: VmTypes.DeletePayload) => {
      return VoicemailApi.deleteVoicemailMedia(req);
    },
    {
      onSuccess: () => {
        if (panelType === 'voicemailTranscription') {
          setShow(false);
        }
      },
    }
  );

  const fetchVmTagData = (queryKey: string[]) => {
    const recordData = queryClient.getQueryData([
      selectedLocationIds,
      ...queryKey,
    ]) as typeof voicemailDetailsQuery.data;
    return recordData?.tagIds || [];
  };

  useEffect(() => {
    const voicemailData = voicemailDetailsQuery.data;
    if (voicemailData) {
      const uniqueKey = `${voicemailData?.channelId}-${voicemailData?.mailboxId}-${voicemailData?.mediaId}-${voicemailData?.voicemailId}`;
      setShow(true, 'voicemailTranscription', {
        rows: [{ ...voicemailData }],
        currentRowId: uniqueKey || '',
        instanceId: null,
        toggleRowSelected: () => {},
        updateReadStatus: updateReadStatusAsync,
        deleteVoicemail: onDelete,
        voicemailsQueryKey: voicemailDetailsQuery.queryKey,
        optimisticApplyTag,
        optimisticRemoveTag,
        sectionFocus: showPanel ? (context as any)?.sectionFocus : 'transcript',
        // userHasAccess will be default to true when it is not provided. We will not show the voicemail transcription if userHasAccess is false.
        // The userHasAccess will be false only if the vmbox is not shared and is tagged where other tagged users dont have access to it.
        userHasAccess: isVoicemailTagsAlertEvent(pendingViewParams) ? pendingViewParams?.userHasAccess ?? true : true,
        fetchTagData: fetchVmTagData,
      });
      props.closeModal();
      if (!notification.hasRead) {
        notificationTrayContext.toggleNotificationAsRead(notification.id, notification.hasRead);
      }
    }
  }, [voicemailDetailsQuery.data, pendingViewParams]);

  const notificationAction = () => {
    if (!notification.alert.payload) return;
    const { payload: params } = notification.alert;
    if (pendingViewParams?.voicemailMessageId === params.voicemailMessageId) {
      voicemailDetailsQuery.refetch();
    } else {
      setPendingViewParams(params);
    }
  };

  return { notificationAction };
};

const isVoicemailTagsAlertEvent = (
  params: VoicemailTagsAlertEvent | VoicemailMessageAlertEvent | null
): params is VoicemailTagsAlertEvent => {
  return !!params && typeof params === 'object' && 'userHasAccess' in params;
};
